/**
 * Custom master data groups/types and tracking items.
 * Keep in lockstep with erp_api/routes/controllers/customMasterData.ts.
 */
import {
  RESERVED_STATIC_MASTER_DATA_GROUP_IDS,
} from './builtinCustomMasterData';

export const CUSTOM_MASTER_DATA_FIELD_INPUT_TYPES = [
  'text',
  'number',
  'percent',
  'yes_no',
  'private_picture',
  /** Public http(s) image URL — stored as plain text for display trackers. */
  'picture_url',
  /** Date or date-span for calendars and seasonal rules. */
  'calendar',
] as const;

export type CustomMasterDataFieldInputType = typeof CUSTOM_MASTER_DATA_FIELD_INPUT_TYPES[number];

/** User-picked tracking items on field definitions (not automatic scope). */
export const MASTER_DATA_TRACKING_ITEMS = [
  'track_measure_category',
  'track_uom',
  'track_cost_price',
  'track_selling_price',
  'track_percent',
  'track_defined',
  'track_boolean',
  'track_number',
  'track_asset_mobility',
  /** Legacy hospitality trackers — kept for restore / old log rows. */
  'track_housekeeping',
  'track_meal_service',
  'track_meal_breakfast',
  'track_meal_lunch',
  'track_meal_dinner',
  'track_customer_id_doc',
  'track_customer_face_photo',
  'track_customer_booking_count',
  'track_customer_lifetime_bookings',
  /** Active / on-hold state for room assets and room-type groups. */
  'track_active_state',
  /** GL account name on template gl_account rows — stored on /logs tracking_items. */
  'track_gl',
  /** Work-order / form photo answers — comma-separated photos-table ids. */
  'track_photo',
  /** Public picture URL on products, assets, SKUs, and custom master data. */
  'track_picture_url',
] as const;

/** Injected on save — not user-picked. track_scope stays for operating custom data.
 * track_compute marks log payloads from other computes via the tracking webhook.
 * Money amounts live on activity_logs.value — not a tracker. */
export const AUTOMATIC_MASTER_DATA_TRACKING_ITEMS = [
  'track_scope',
  'track_sku',
  'track_config',
  'track_head_scope',
  'track_object',
  'track_email',
  'track_compute',
] as const;

export type UserMasterDataTrackingItem = typeof MASTER_DATA_TRACKING_ITEMS[number];
export type AutomaticMasterDataTrackingItem = typeof AUTOMATIC_MASTER_DATA_TRACKING_ITEMS[number];
export type MasterDataTrackingItem = UserMasterDataTrackingItem | AutomaticMasterDataTrackingItem;

export const AUTOMATIC_SCOPE_FIELD_KEY = '_scope';
export const AUTOMATIC_OBJECT_FIELD_KEY = '_object';

export const isAutomaticTrackingFieldKey = (key: string): boolean => (
  key === AUTOMATIC_SCOPE_FIELD_KEY || key === AUTOMATIC_OBJECT_FIELD_KEY
);

export { trackerFieldKey } from './trackerFieldKey';
import { trackerFieldKey } from './trackerFieldKey';

export const CUSTOM_MASTER_DATA_TIERS = [
  'structural',
  'configuration',
  'operation',
  'sku',
] as const;

export type CustomMasterDataTier = typeof CUSTOM_MASTER_DATA_TIERS[number];

/** Tiers the New data form may pick — Structure custom types become head scopes. */
export const CUSTOM_MASTER_DATA_USER_TIERS = [
  'structural',
  'configuration',
  'operation',
  'sku',
] as const;

/** Sidebar tier tabs — SKU logic tier maps to operation VO, not a header tab. */
export const CUSTOM_MASTER_DATA_HEADER_TIERS = [
  'structural',
  'configuration',
  'operation',
] as const;

export type CustomMasterDataHeaderTier = typeof CUSTOM_MASTER_DATA_HEADER_TIERS[number];

export type CustomMasterDataFieldDef = {
  key: string
  label: string
  inputType: CustomMasterDataFieldInputType
  required?: boolean
  max?: number
  trackingItem?: MasterDataTrackingItem
  /** Master data type codes or built-in kinds this field may link to (multi). */
  linkKinds?: string[]
};

export type CustomMasterDataLinkRules = {
  /**
   * Structural head-scope group this ERP data sits under (required for non-structural).
   * Parent groups and this item both hang under that head scope.
   */
  headScopeTypeCode?: string
  /** Parent scope custom data type codes (non-SKU, non-head-scope parent groups, multi). */
  parentTypeCodes?: string[]
  /** @deprecated legacy single parent — migrated to parentTypeCodes. */
  parentTypeCode?: string
  /** Linked SKU custom data type code (XOR with parentTypeCodes). */
  skuTypeCode?: string
  /** SKU tier links to custom data type with UOM tracking. */
  uomTypeCode?: string
  /** Non-parent attach targets (custom types or built-in record kinds, multi). */
  attachedMasterTypeCodes?: string[]
  /** Attached types that allow many rows per parent record (e.g. asset group links). */
  multipleAttachedMasterTypeCodes?: string[]
  /** @deprecated use attachedMasterTypeCodes */
  attachedCustomTypeCodes?: string[]
  /** @deprecated use multipleAttachedMasterTypeCodes */
  multipleAttachedCustomTypeCodes?: string[]
  /** @deprecated legacy single attach — migrated to attachedMasterTypeCodes. */
  attachedCustomTypeCode?: string
  /** @deprecated legacy alias for attachedCustomTypeCode. */
  configurationTypeCode?: string
  allowedLinkTypeCodes?: string[]
};

export type CustomMasterDataGroup = {
  id: string
  code: string
  name: string
  tier: CustomMasterDataHeaderTier
  subgroup: string | null
  /** Built-in record kinds shown under this header group. */
  templateKinds?: string[]
  isBuiltin?: boolean
  sortOrder?: number
};

export type CustomMasterDataType = {
  id: string
  code: string
  name: string
  tier: CustomMasterDataTier
  groupId: string
  fields: CustomMasterDataFieldDef[]
  templateSourceKind?: string
  linkRules?: CustomMasterDataLinkRules
  /**
   * Only one structural type per company may set this — attributes organization head scope
   * to this organizational-groups custom data type.
   */
  isOrganizationHeadScopeGroup?: boolean
  sortOrder?: number
};

/** Static org → management kinds — not parent-scope options, not custom-data setup. */
export const HEAD_SCOPE_TEMPLATE_KINDS = [
  'organization',
  'organizational_group',
  'location',
  'department',
  'designation',
  'employee',
  'management',
  'team',
] as const;

export const isHeadScopeCustomMasterDataType = (
  typeDef: Pick<CustomMasterDataType, 'templateSourceKind' | 'tier'>,
): boolean => {
  // User structural custom data is a head scope once created.
  if (typeDef.tier === 'structural') return true;
  const source = String(typeDef.templateSourceKind || '').trim().toLowerCase();
  return (HEAD_SCOPE_TEMPLATE_KINDS as readonly string[]).includes(source);
};

/** Built-in Structure templates (Organization, Location, …) — valid head-scope group codes. */
export const isHeadScopeTemplateKindCode = (raw: unknown): boolean => {
  const code = String(raw || '').trim().toLowerCase();
  return (HEAD_SCOPE_TEMPLATE_KINDS as readonly string[]).includes(code);
};

export type CustomMasterDataPayload = {
  groups: CustomMasterDataGroup[]
  types: CustomMasterDataType[]
};

export const TRACKING_ITEM_LABEL: Record<MasterDataTrackingItem, string> = {
  track_measure_category: 'Measure category',
  track_uom: 'UOM (category + conversion)',
  track_cost_price: 'Cost price',
  track_selling_price: 'Selling price',
  track_percent: 'Percent',
  track_defined: 'Defined scope',
  track_scope: 'Scope (text)',
  track_sku: 'SKU',
  track_config: 'Configuration',
  track_head_scope: 'Head scope',
  track_object: 'Object',
  track_email: 'Email',
  track_compute: 'Compute (external)',
  track_boolean: 'Yes / No',
  track_number: 'Number',
  track_asset_mobility: 'Asset mobility',
  track_housekeeping: 'Housekeeping',
  track_meal_service: 'Meal service',
  track_meal_breakfast: 'Breakfast',
  track_meal_lunch: 'Lunch',
  track_meal_dinner: 'Dinner',
  track_customer_id_doc: 'Customer ID document',
  track_customer_face_photo: 'Customer face photo',
  track_customer_booking_count: 'Customer booking count',
  track_customer_lifetime_bookings: 'Customer lifetime bookings',
  track_active_state: 'Active state',
  track_gl: 'GL account name',
  track_photo: 'Form photo',
  track_picture_url: 'Picture URL',
};

export const CUSTOM_FIELD_INPUT_LABEL: Record<CustomMasterDataFieldInputType, string> = {
  text: 'Text',
  number: 'Number',
  percent: 'Percent',
  yes_no: 'Yes / No',
  private_picture: 'Private picture',
  picture_url: 'Picture URL',
  calendar: 'Calendar',
};

/** Private picture fields store secret-file-style JSON metadata (not a public URL). */
export const CUSTOM_MASTER_PRIVATE_PICTURE_VALUE_MAX = 4000;

/** Public picture URL — http(s) or data:image string. */
export const CUSTOM_MASTER_PICTURE_URL_VALUE_MAX = 500;

export const TRACK_PICTURE_URL = 'track_picture_url' as const;

export const PICTURE_URL_FIELD_KEY = trackerFieldKey(TRACK_PICTURE_URL, 'picture_url');

/** Reusable picture URL field — add many on any custom master data type (unique label → key). */
export const pictureUrlCustomFieldDef = (label = 'Picture URL'): CustomMasterDataFieldDef => ({
  key: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40) || PICTURE_URL_FIELD_KEY,
  label: label.slice(0, 80),
  inputType: 'picture_url',
  max: CUSTOM_MASTER_PICTURE_URL_VALUE_MAX,
  trackingItem: TRACK_PICTURE_URL,
});

export const isPictureUrlFieldValue = (raw: unknown): boolean => {
  const value = String(raw || '').trim();
  if (!value) return true;
  return /^https?:\/\//i.test(value) || value.startsWith('data:image');
};

export type CustomMasterPrivatePictureValue = {
  gcsPath: string
  fileName: string
  sizeBytes: number
  contentType: string
};

export const parseCustomMasterPrivatePictureValue = (
  raw: unknown,
): CustomMasterPrivatePictureValue | null => {
  let src: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      src = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!src || typeof src !== 'object' || Array.isArray(src)) return null;
  const row = src as Record<string, unknown>;
  const gcsPath = String(row.gcsPath || '').trim();
  const fileName = String(row.fileName || '').trim();
  const sizeBytes = Number(row.sizeBytes);
  const contentType = String(row.contentType || 'application/octet-stream').trim()
    || 'application/octet-stream';
  if (!gcsPath || !fileName || !Number.isFinite(sizeBytes) || sizeBytes < 0) return null;
  return { gcsPath, fileName, sizeBytes, contentType };
};

export const stringifyCustomMasterPrivatePictureValue = (
  value: CustomMasterPrivatePictureValue,
): string => JSON.stringify({
  gcsPath: String(value.gcsPath || '').trim().slice(0, 500),
  fileName: String(value.fileName || '').trim().slice(0, 240),
  sizeBytes: Math.max(0, Math.floor(Number(value.sizeBytes) || 0)),
  contentType: String(value.contentType || 'application/octet-stream').trim().slice(0, 120)
    || 'application/octet-stream',
});

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
);

const clip = (value: unknown, max: number): string => (
  String(value ?? '').trim().slice(0, max)
);

const isInputType = (value: unknown): value is CustomMasterDataFieldInputType => (
  CUSTOM_MASTER_DATA_FIELD_INPUT_TYPES.includes(value as CustomMasterDataFieldInputType)
);

const ALL_TRACKING_ITEMS: MasterDataTrackingItem[] = [
  ...MASTER_DATA_TRACKING_ITEMS,
  ...AUTOMATIC_MASTER_DATA_TRACKING_ITEMS,
];

const isTrackingItem = (value: unknown): value is MasterDataTrackingItem => (
  ALL_TRACKING_ITEMS.includes(value as MasterDataTrackingItem)
);

/** Public guard — any user or automatic tracking item id. */
export const isMasterDataTrackingItem = (value: unknown): value is MasterDataTrackingItem => (
  isTrackingItem(value)
);

const isUserTrackingItem = (value: unknown): value is UserMasterDataTrackingItem => (
  MASTER_DATA_TRACKING_ITEMS.includes(value as UserMasterDataTrackingItem)
);

const isHeaderTier = (value: unknown): value is CustomMasterDataHeaderTier => (
  CUSTOM_MASTER_DATA_HEADER_TIERS.includes(value as CustomMasterDataHeaderTier)
);

const isTier = (value: unknown): value is CustomMasterDataTier => (
  CUSTOM_MASTER_DATA_TIERS.includes(value as CustomMasterDataTier)
);

export const sanitizeCustomMasterDataField = (raw: unknown): CustomMasterDataFieldDef | null => {
  const src = asRecord(raw);
  const label = clip(src.label, 80);
  const key = clip(src.key, 80) || label.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40);
  if (!label || !key) return null;
  const inputType = isInputType(src.inputType) ? src.inputType : 'text';
  const trackingItem = isUserTrackingItem(src.trackingItem) ? src.trackingItem : undefined;
  const linkKinds = Array.isArray(src.linkKinds)
    ? src.linkKinds.map((item) => clip(item, 80)).filter(Boolean).slice(0, 24)
    : undefined;
  // Private pictures store JSON metadata — keep room for gcsPath / fileName / size.
  // Calendar values are ISO dates or short spans (e.g. 2026-01-01..2026-01-07).
  const defaultMax = inputType === 'private_picture'
    ? CUSTOM_MASTER_PRIVATE_PICTURE_VALUE_MAX
    : (inputType === 'picture_url'
      ? CUSTOM_MASTER_PICTURE_URL_VALUE_MAX
      : (inputType === 'calendar' ? 64 : undefined));
  return {
    key,
    label,
    inputType,
    required: src.required === true,
    max: Number(src.max) > 0
      ? Math.min(Number(src.max), 500000)
      : defaultMax,
    trackingItem,
    linkKinds: linkKinds?.length ? linkKinds : undefined,
  };
};

/** Internal group code — not shown in UI; derived from id or name. */
export const deriveCustomMasterDataGroupCode = (id: string, name: string): string => {
  const fromId = String(id || '').trim();
  if (fromId.startsWith('static_')) {
    return fromId.replace(/^static_/, '').toUpperCase().slice(0, 40);
  }
  if (fromId.startsWith('builtin_')) {
    return fromId.replace(/^builtin_/, '').toUpperCase().slice(0, 40);
  }
  const fromName = String(name || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').slice(0, 40);
  return fromName || fromId.slice(0, 40).toUpperCase() || 'GROUP';
};

export const sanitizeCustomMasterDataGroup = (raw: unknown, index = 0): CustomMasterDataGroup | null => {
  const src = asRecord(raw);
  const name = clip(src.name, 80);
  const tier = isHeaderTier(src.tier) ? src.tier : 'operation';
  const id = clip(src.id, 80) || `grp_${index + 1}`;
  if (!name) return null;
  const code = clip(src.code, 40).toUpperCase() || deriveCustomMasterDataGroupCode(id, name);
  const templateKinds = Array.isArray(src.templateKinds)
    ? src.templateKinds.map((item) => clip(item, 80)).filter(Boolean)
    : undefined;
  return {
    id,
    code,
    name,
    tier,
    subgroup: clip(src.subgroup, 80) || null,
    templateKinds: templateKinds?.length ? templateKinds : undefined,
    isBuiltin: src.isBuiltin === true,
    sortOrder: Number.isFinite(Number(src.sortOrder)) ? Number(src.sortOrder) : index,
  };
};

export const sanitizeCustomMasterDataType = (raw: unknown, index = 0): CustomMasterDataType | null => {
  const src = asRecord(raw);
  const code = clip(src.code, 40).toUpperCase();
  const name = clip(src.name, 80);
  const tier = isTier(src.tier) ? src.tier : 'operation';
  const groupId = clip(src.groupId, 80);
  if (!code || !name || !groupId) return null;
  const fieldsRaw = Array.isArray(src.fields) ? src.fields : [];
  const fields = fieldsRaw
    .map((item) => sanitizeCustomMasterDataField(item))
    .filter(Boolean) as CustomMasterDataFieldDef[];
  const linkRulesRaw = asRecord(src.linkRules);
  const parentTypeCodesRaw = Array.isArray(linkRulesRaw.parentTypeCodes)
    ? linkRulesRaw.parentTypeCodes
    : linkRulesRaw.parentTypeCode
      ? [linkRulesRaw.parentTypeCode]
      : [];
  const parentTypeCodes = parentTypeCodesRaw
    .map((item) => clip(item, 40).toUpperCase())
    .filter(Boolean)
    .slice(0, 24);
  // Structural / head-scope custom data cannot link to an SKU.
  const skuTypeCode = tier === 'structural'
    ? undefined
    : (clip(linkRulesRaw.skuTypeCode, 40).toUpperCase() || undefined);
  const uomTypeCode = tier === 'structural'
    ? undefined
    : (clip(linkRulesRaw.uomTypeCode, 40).toUpperCase() || undefined);
  // Prefer multi attach array; fold legacy keys into attachedMasterTypeCodes.
  const attachedFromMaster = Array.isArray(linkRulesRaw.attachedMasterTypeCodes)
    ? linkRulesRaw.attachedMasterTypeCodes
    : [];
  const attachedFromMasterCodes = attachedFromMaster
    .map((item) => clip(item, 40).toUpperCase())
    .filter(Boolean);
  const attachedFromArray = Array.isArray(linkRulesRaw.attachedCustomTypeCodes)
    ? linkRulesRaw.attachedCustomTypeCodes
    : [];
  const legacyAttachSingle = clip(
    linkRulesRaw.attachedCustomTypeCode || linkRulesRaw.configurationTypeCode,
    40,
  ).toUpperCase();
  const attachedMasterTypeCodes = Array.from(new Set([
    ...attachedFromMasterCodes,
    ...attachedFromArray.map((item) => clip(item, 40).toUpperCase()).filter(Boolean),
    ...(legacyAttachSingle ? [legacyAttachSingle] : []),
  ])).slice(0, 24);
  const multiFromMaster = Array.isArray(linkRulesRaw.multipleAttachedMasterTypeCodes)
    ? linkRulesRaw.multipleAttachedMasterTypeCodes
    : [];
  const multiFromMasterCodes = multiFromMaster
    .map((item) => clip(item, 40).toUpperCase())
    .filter(Boolean);
  const multiFromLegacy = Array.isArray(linkRulesRaw.multipleAttachedCustomTypeCodes)
    ? linkRulesRaw.multipleAttachedCustomTypeCodes
      .map((item) => clip(item, 40).toUpperCase())
      .filter(Boolean)
    : [];
  const multipleAttachedMasterTypeCodes = Array.from(new Set([
    ...multiFromMasterCodes,
    ...multiFromLegacy,
  ])).slice(0, 24);
  const isOrganizationHeadScopeGroup = tier === 'structural' && (
    src.isOrganizationHeadScopeGroup === true
    || clip(src.templateSourceKind, 80).toLowerCase() === 'organization'
  );
  const headScopeTypeCode = tier === 'structural'
    ? undefined
    : (clip(linkRulesRaw.headScopeTypeCode, 40).toUpperCase() || undefined);
  const linkRules: CustomMasterDataLinkRules | undefined = (
    headScopeTypeCode
    || parentTypeCodes.length || skuTypeCode || uomTypeCode || attachedMasterTypeCodes.length
    || multipleAttachedMasterTypeCodes.length
    || linkRulesRaw.allowedLinkTypeCodes
  ) ? {
      headScopeTypeCode,
      parentTypeCodes: parentTypeCodes.length ? parentTypeCodes : undefined,
      skuTypeCode,
      uomTypeCode,
      attachedMasterTypeCodes: attachedMasterTypeCodes.length ? attachedMasterTypeCodes : undefined,
      multipleAttachedMasterTypeCodes: multipleAttachedMasterTypeCodes.length
        ? multipleAttachedMasterTypeCodes
        : undefined,
      allowedLinkTypeCodes: Array.isArray(linkRulesRaw.allowedLinkTypeCodes)
        ? linkRulesRaw.allowedLinkTypeCodes.map((item) => clip(item, 40)).filter(Boolean)
        : undefined,
    } : undefined;
  return {
    id: clip(src.id, 80) || `typ_${index + 1}`,
    code,
    name,
    tier,
    groupId,
    fields,
    templateSourceKind: isOrganizationHeadScopeGroup
      ? 'organization'
      : (clip(src.templateSourceKind, 80) || undefined),
    linkRules,
    isOrganizationHeadScopeGroup: isOrganizationHeadScopeGroup || undefined,
    sortOrder: Number.isFinite(Number(src.sortOrder)) ? Number(src.sortOrder) : index,
  };
};

export const sanitizeCustomMasterDataPayload = (raw: unknown): CustomMasterDataPayload => {
  const src = asRecord(raw);
  const groupsRaw = Array.isArray(src.groups) ? src.groups : [];
  const typesRaw = Array.isArray(src.types) ? src.types : [];
  const groups = groupsRaw
    .map((item, index) => sanitizeCustomMasterDataGroup(item, index))
    .filter(Boolean) as CustomMasterDataGroup[];
  const types = typesRaw
    .map((item, index) => sanitizeCustomMasterDataType(item, index))
    .filter(Boolean) as CustomMasterDataType[];
  return { groups, types };
};

export const newCustomMasterDataId = (prefix: string): string => (
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
);

/** ERP-style unique code — monotonic BASE-001, BASE-002 (gaps are not reused). */
export const generateCustomDataCode = (
  existingCodes: Iterable<string>,
  nameHint?: string,
): string => {
  const base = String(nameHint || 'ERP')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24) || 'ERP';
  const used = new Set(Array.from(existingCodes).map((code) => String(code || '').trim().toUpperCase()));
  const suffixPattern = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(\\d+)$`);
  let maxSeq = 0;
  used.forEach((code) => {
    const match = code.match(suffixPattern);
    if (match) maxSeq = Math.max(maxSeq, Number(match[1]) || 0);
  });
  let seq = maxSeq + 1;
  let candidate = `${base}-${String(seq).padStart(3, '0')}`;
  while (used.has(candidate)) {
    seq += 1;
    candidate = `${base}-${String(seq).padStart(3, '0')}`;
  }
  return candidate;
};

export const customMasterDataTypeHasUomTracking = (typeDef: CustomMasterDataType): boolean => (
  typeDef.fields.some((field) => field.trackingItem === 'track_uom')
);

export const isSkuTierCustomMasterDataType = (typeDef: CustomMasterDataType): boolean => (
  typeDef.tier === 'sku'
);

export const automaticScopeTrackingItemForTier = (
  tier: CustomMasterDataTier,
): AutomaticMasterDataTrackingItem => {
  if (tier === 'sku') return 'track_sku';
  if (tier === 'structural') return 'track_head_scope';
  if (tier === 'configuration') return 'track_config';
  return 'track_scope';
};

/** Auto trackers: SKU / config / operating scope / head scope, plus object on every custom type. */
export const ensureAutomaticTrackingFields = (
  fields: CustomMasterDataFieldDef[],
  tier: CustomMasterDataTier,
): CustomMasterDataFieldDef[] => {
  const withoutAuto = fields.filter((field) => !isAutomaticTrackingFieldKey(field.key));
  const scopeTracking = automaticScopeTrackingItemForTier(tier);
  return [
    ...withoutAuto,
    {
      key: AUTOMATIC_SCOPE_FIELD_KEY,
      label: tier === 'structural' ? 'Head scope' : 'Scope',
      inputType: 'text',
      required: false,
      trackingItem: scopeTracking,
    },
    {
      key: AUTOMATIC_OBJECT_FIELD_KEY,
      label: 'Object',
      inputType: 'text',
      required: false,
      trackingItem: 'track_object',
    },
  ];
};

/** @deprecated use ensureAutomaticTrackingFields — kept name for older call sites. */
export const ensureAutomaticScopeTrackingField = (
  fields: CustomMasterDataFieldDef[],
  tier: CustomMasterDataTier = 'operation',
): CustomMasterDataFieldDef[] => ensureAutomaticTrackingFields(fields, tier);

export const normalizeCustomMasterDataLinkRules = (
  linkRules?: CustomMasterDataLinkRules,
): CustomMasterDataLinkRules | undefined => {
  if (!linkRules) return undefined;
  const headScopeTypeCode = String(linkRules.headScopeTypeCode || '').trim().toUpperCase() || undefined;
  const parentTypeCodes = (linkRules.parentTypeCodes || [])
    .map((code) => String(code || '').trim().toUpperCase())
    .filter(Boolean);
  const legacy = String(linkRules.parentTypeCode || '').trim().toUpperCase();
  const mergedParents = Array.from(new Set([
    ...parentTypeCodes,
    ...(legacy ? [legacy] : []),
  ])).slice(0, 24);
  const skuTypeCode = String(linkRules.skuTypeCode || '').trim().toUpperCase() || undefined;
  const uomTypeCode = String(linkRules.uomTypeCode || '').trim().toUpperCase() || undefined;
  const attachedFromMaster = (linkRules.attachedMasterTypeCodes || [])
    .map((code) => String(code || '').trim().toUpperCase())
    .filter(Boolean);
  const attachedFromArray = (linkRules.attachedCustomTypeCodes || [])
    .map((code) => String(code || '').trim().toUpperCase())
    .filter(Boolean);
  const legacyAttach = String(
    linkRules.attachedCustomTypeCode || linkRules.configurationTypeCode || '',
  ).trim().toUpperCase();
  const attachedMasterTypeCodes = Array.from(new Set([
    ...attachedFromMaster,
    ...attachedFromArray,
    ...(legacyAttach ? [legacyAttach] : []),
  ])).slice(0, 24);
  const multiFromMaster = (linkRules.multipleAttachedMasterTypeCodes || [])
    .map((code) => String(code || '').trim().toUpperCase())
    .filter(Boolean);
  const multiFromLegacy = (linkRules.multipleAttachedCustomTypeCodes || [])
    .map((code) => String(code || '').trim().toUpperCase())
    .filter(Boolean);
  const multipleAttachedMasterTypeCodes = Array.from(new Set([
    ...multiFromMaster,
    ...multiFromLegacy,
  ])).slice(0, 24);
  if (
    !headScopeTypeCode
    && !mergedParents.length
    && !skuTypeCode
    && !uomTypeCode
    && !attachedMasterTypeCodes.length
    && !multipleAttachedMasterTypeCodes.length
    && !linkRules.allowedLinkTypeCodes?.length
  ) {
    return undefined;
  }
  return {
    headScopeTypeCode,
    parentTypeCodes: mergedParents.length ? mergedParents : undefined,
    skuTypeCode,
    uomTypeCode,
    attachedMasterTypeCodes: attachedMasterTypeCodes.length ? attachedMasterTypeCodes : undefined,
    multipleAttachedMasterTypeCodes: multipleAttachedMasterTypeCodes.length
      ? multipleAttachedMasterTypeCodes
      : undefined,
    allowedLinkTypeCodes: linkRules.allowedLinkTypeCodes,
  };
};

/** Parent scopes XOR SKU; every non-structural ERP data must sit under a structural head-scope group. */
export const validateCustomMasterDataLinkRules = (
  typeDef: Pick<CustomMasterDataType, 'id' | 'code' | 'tier' | 'linkRules'>,
  allTypes: CustomMasterDataType[],
): string | null => {
  const others = allTypes.filter((item) => item.id !== typeDef.id);
  const rules = normalizeCustomMasterDataLinkRules(typeDef.linkRules);
  const codeByType = new Map(allTypes.map((item) => [item.code.toUpperCase(), item]));

  // Non-structural ERP data must hang under a structural head-scope group (like template Structure).
  if (typeDef.tier !== 'structural') {
    const headCode = String(rules?.headScopeTypeCode || '').trim().toUpperCase();
    if (!headCode) {
      return 'Pick a structure head-scope group for this ERP data';
    }
    // Allow Organization / Location / … templates as well as custom Structure ERP data.
    if (!isHeadScopeTemplateKindCode(headCode)) {
      const headType = codeByType.get(headCode);
      if (!headType || headType.tier !== 'structural') {
        return 'Head-scope group must be structure ERP data or a Structure template';
      }
      if (headType.id === typeDef.id) {
        return 'ERP data cannot use itself as its head-scope group';
      }
    }
  }

  if (!others.length) {
    if (typeDef.tier === 'sku' && !rules?.uomTypeCode) {
      return 'SKU custom data must link to custom data with UOM tracking';
    }
    if (typeDef.tier !== 'structural' && !rules?.headScopeTypeCode) {
      return 'Create a structure head-scope group first, then link this ERP data under it';
    }
    if (rules?.attachedMasterTypeCodes?.length) {
      return 'Cannot attach — no other custom data types exist yet';
    }
    return null;
  }
  const parentCodes = rules?.parentTypeCodes || [];
  const skuCode = rules?.skuTypeCode || '';
  const hasParents = parentCodes.length > 0;
  const hasSku = Boolean(skuCode);
  if (typeDef.tier === 'structural' && hasSku) {
    return 'Structure custom data cannot link to an SKU';
  }
  const skipLinkXor = typeDef.tier === 'structural';
  if (!skipLinkXor && hasParents && hasSku) {
    return 'Pick either parent scopes or an SKU data, not both';
  }
  if (hasSku) {
    const skuType = codeByType.get(skuCode);
    if (!skuType || skuType.tier !== 'sku') return 'Pick a valid SKU custom data type';
    if (skuType.id === typeDef.id) return 'A custom data type cannot link to itself as SKU';
  }
  if (hasParents) {
    for (const parentCode of parentCodes) {
      const parentType = codeByType.get(parentCode);
      if (!parentType) return `Unknown parent custom data type ${parentCode}`;
      if (parentType.tier === 'sku') return 'SKU datas cannot be parent scopes';
      if (isHeadScopeCustomMasterDataType(parentType)) {
        return 'Head scopes cannot be parent groups — set Head-scope group instead';
      }
      if (parentType.id === typeDef.id) return 'A custom data type cannot be its own parent scope';
      const parentHead = String(
        normalizeCustomMasterDataLinkRules(parentType.linkRules)?.headScopeTypeCode || '',
      ).trim().toUpperCase();
      const myHead = String(rules?.headScopeTypeCode || '').trim().toUpperCase();
      if (parentHead && myHead && parentHead !== myHead) {
        return 'Parent group must use the same structure head-scope group';
      }
    }
  }
  if (typeDef.tier === 'sku' && !rules?.uomTypeCode) {
    return 'SKU custom data must link to custom data with UOM tracking';
  }
  for (const attachedCode of rules?.attachedMasterTypeCodes || []) {
    const attachedType = codeByType.get(attachedCode);
    if (!attachedType) return `Unknown custom data type ${attachedCode}`;
    if (isHeadScopeCustomMasterDataType(attachedType)) {
      return 'Cannot attach to head-scope custom data';
    }
    if (attachedType.id === typeDef.id) {
      return 'A custom data type cannot attach to itself';
    }
  }
  return null;
};

/** Only one structural type may carry organization head scope for company org groups. */
export const validateOrganizationHeadScopeGroupUnique = (
  typeDef: Pick<CustomMasterDataType, 'id' | 'tier' | 'isOrganizationHeadScopeGroup'>,
  allTypes: CustomMasterDataType[],
): string | null => {
  if (!typeDef.isOrganizationHeadScopeGroup) return null;
  if (typeDef.tier !== 'structural') {
    return 'Organization head scope groups must be structure custom data';
  }
  const other = allTypes.find((item) => (
    item.id !== typeDef.id && item.isOrganizationHeadScopeGroup === true
  ));
  if (other) {
    return `Organization head scope is already on “${other.name}” — only one custom data type can have it`;
  }
  return null;
};

export const parentScopeCodesInUse = (
  types: CustomMasterDataType[],
  exceptTypeId?: string,
): Set<string> => {
  const used = new Set<string>();
  types.forEach((typeDef) => {
    if (exceptTypeId && typeDef.id === exceptTypeId) return;
    normalizeCustomMasterDataLinkRules(typeDef.linkRules)?.parentTypeCodes?.forEach((code) => {
      used.add(code.toUpperCase());
    });
  });
  return used;
};

/** Tracking snapshots written on record add/change logs — charts/audits read later. */
export type MasterDataLogTrackingItem = {
  trackingItem: MasterDataTrackingItem
  fieldKey: string
  recordKind: string
  recordCode: string
  value: string
};

export type PinnedCustomMasterDataTab = {
  typeCode: string
  label: string
  navGroup: 'Configuration master data'
  subgroup: 'Custom'
  groupId: string
};

/** Pinned custom-data tabs show instance lists (not the type editor). */
export const PINNED_CUSTOM_MASTER_DATA_TABS: PinnedCustomMasterDataTab[] = [];

export const pinnedCustomMasterDataTabByCode = (
  code: string,
): PinnedCustomMasterDataTab | null => (
  PINNED_CUSTOM_MASTER_DATA_TABS.find(
    (tab) => tab.typeCode.toUpperCase() === String(code || '').trim().toUpperCase(),
  ) || null
);

export const isStaticCustomMasterDataGroupId = (groupId: unknown): boolean => (
  (RESERVED_STATIC_MASTER_DATA_GROUP_IDS as readonly string[]).includes(String(groupId || '').trim())
);

/** True when a row is a nav folder / group label — not leaf custom data you attach to a record. */
export const isCustomMasterDataGroupFolderType = (
  typeDef: CustomMasterDataType,
  groups: CustomMasterDataGroup[] = [],
): boolean => {
  const id = String(typeDef.id || '').trim();
  const code = String(typeDef.code || '').trim().toUpperCase();
  const name = String(typeDef.name || '').trim();
  if (groups.some((group) => group.id === id)) return true;
  if (groups.some((group) => String(group.code || '').trim().toUpperCase() === code)) return true;
  if (pinnedCustomMasterDataTabByCode(code)) return true;
  const parentGroup = groups.find((group) => group.id === typeDef.groupId);
  if (
    parentGroup
    && parentGroup.name === name
    && isStaticCustomMasterDataGroupId(parentGroup.id)
  ) {
    return true;
  }
  return false;
};

/** True when other types list this one as a parent scope — pick the children instead. */
export const isCustomMasterDataParentScopeGroupType = (
  typeDef: CustomMasterDataType,
  allTypes: CustomMasterDataType[],
): boolean => {
  const code = String(typeDef.code || '').trim().toUpperCase();
  if (!code) return false;
  return allTypes.some((other) => {
    if (other.id === typeDef.id) return false;
    const parents = normalizeCustomMasterDataLinkRules(other.linkRules)?.parentTypeCodes || [];
    return parents.some((parentCode) => parentCode.toUpperCase() === code);
  });
};

/** Leaf custom data types that may be attached on a record (Connect custom data / link rules). */
export const isAttachableCustomMasterDataType = (
  typeDef: CustomMasterDataType,
  allTypes: CustomMasterDataType[],
  groups: CustomMasterDataGroup[] = [],
): boolean => {
  if (isHeadScopeCustomMasterDataType(typeDef)) return false;
  if (String(typeDef.templateSourceKind || '').trim()) return false;
  if (isCustomMasterDataGroupFolderType(typeDef, groups)) return false;
  if (isCustomMasterDataParentScopeGroupType(typeDef, allTypes)) return false;
  return true;
};
